import React, { Component, Suspense } from "react";
import { inject } from "@vercel/analytics";
import { ConfigProvider } from "antd";
import enUS from "antd/lib/locale/en_US";
import deDE from "antd/lib/locale/de_DE";
import dayjs from "dayjs";
import { HelmetProvider } from "react-helmet-async";
import withAuthProvider, {
  AuthComponentProps,
  AuthComponentState,
} from "./stores/AuthProvider";
import { HashRouter as Router, Route, Switch } from "react-router-dom";
import { createHashHistory } from "history";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { detect, fromNavigator } from "@lingui/detect-locale";
import numeral from "./lib/numeral";
import is from "is";
import { getCookie } from "tiny-cookie";
import { messages as enMessages } from "./locales/en/messages";
import { messages as deMessages } from "./locales/de/messages";
import LoadingAnimation from "./components/LoadingAnimation";

import "./scss/style.scss";
import "dayjs/locale/de";
import store from "./stores/StoreRoot";

// add vercel analytics
inject();

const Welcome = React.lazy(() => import("./views/welcome/Welcome"));

function whatDecimalSeparator() {
  var n = 1.1;
  let separator = n.toLocaleString().substring(1, 2);
  return separator;
}

function getLanguage() {
  const lang =
    localStorage.getItem("userLanguage") || detect(fromNavigator(), "en-US");

  const separator = whatDecimalSeparator();
  console.log("INFO decimal separator", separator);

  if (lang?.includes("de")) return "de";
  if (lang?.includes("fr")) return "fr";

  return "en";
}

export const history = createHashHistory();

i18n.load({
  en: enMessages,
  de: deMessages,
});

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

// Containers
const TheLayout = React.lazy(() => import("./containers/TheLayout"));
console.time("PROFILE - start");

const locales = {
  de: deDE,
  en: enUS,
};

const getLocale = (lang) => {
  if (lang.includes("en")) {
    return locales.en;
  }

  return locales.de;
};

class App extends Component<AuthComponentProps, AuthComponentState> {
  constructor(props) {
    super(props);
    const language = getLanguage();
    console.log("INFO language", language);

    this.state = {
      language: language,
      loading: false,
    };

    this.handleChangeLanguage = this.handleChangeLanguage.bind(this);
    this.onTriggerReload = this.onTriggerReload.bind(this);
    this.activateLanguage = this.activateLanguage.bind(this);

    // set the language
    this.activateLanguage(language);
  }

  componentDidMount() {
    // const { language } = this.state;
    // this.activateLanguage(language);
    const url = window.location.hash ?? "";
    console.log("INFO App Url", url);
    store.checkIsStandalone();
    sessionStorage.setItem("url", url);
    sessionStorage.setItem("isPhone", store.isPhone ? "true" : "false");
    sessionStorage.setItem("isStandalone", store.isStandalone ? "true" : "false");
    if (url !== "#/" || !store.isPhone || !store.isStandalone) return;

    // look for a last App Url cookie
    const lastAppUrl = getCookie("ar-lastAppUrl");
    if (lastAppUrl && is.string(lastAppUrl)) {
      console.log("INFO loading page from lastAppUrl", lastAppUrl);
      store.openUrl(lastAppUrl.replace("#/", "/"), true);
    }
  }

  activateLanguage(lang: string) {
    // is it public
    const hash = window?.location?.hash;
    const isApp = hash ? hash.includes("element/") || hash.includes("sharingwizard/") : false;
    // define the locale
    let language = lang.includes("de") ? "de" : "en";
    if (lang.includes("fr")) language = "fr";
    // german only inside an app
    if (!isApp) language = "en";
    numeral.locale(language);
    i18n.activate(language);
    dayjs.locale(language);

    store.main.setUserLanguage(lang);
  }

  handleChangeLanguage(lang) {
    this.setState({
      language: lang,
    });
    store.main.setUserLanguage(lang);
    // directly switch
    this.activateLanguage(lang);
  }

  onTriggerReload(value) {
    window.location.reload();
  }

  render() {
    const { language } = this.state;
    const locale = getLocale(language);

    if (this.state.loading) return <div>...</div>;

    return (
      <DndProvider backend={HTML5Backend}>
        <I18nProvider i18n={i18n}>
          <HelmetProvider>
            <ConfigProvider
              locale={locale}
              theme={{
                token: {
                  fontFamily: "ProximaSoft",
                },
                // components: {
                //   Statistic: {
                //     titleFontSize: 12,
                //   },
                // },
              }}
            >
              <Router>
                <React.Suspense fallback={loading}>
                  <Switch>
                    <Route
                      path="/"
                      render={(props) =>
                        this.props.isAuthenticated ||
                        this.props.isPublic ||
                        this.props.isTeams ? (
                          <TheLayout
                            {...props}
                            isAuthenticated={this.props.isAuthenticated}
                            user={this.props.user}
                            checkFileAccessRights={
                              this.props.checkFileAccessRights
                            }
                            checkConsentRights={this.props.checkConsentRights}
                            onChangeLanguage={this.handleChangeLanguage}
                            onTriggerReload={this.onTriggerReload}
                            getAccessToken={this.props.getAccessToken}
                            getPickerToken={this.props.getPickerToken}
                            logoutHanko={this.props.logoutHanko}
                            authButtonMethod={
                              this.props.isAuthenticated
                                ? this.props.logout
                                : this.props.login
                            }
                          />
                        ) : (
                          <Suspense fallback={<LoadingAnimation />}>
                            {this.props.isLoaded && (
                              <Welcome
                                {...props}
                                isAuthenticated={this.props.isAuthenticated}
                                user={this.props.user}
                                authButtonMethod={this.props.login}
                                error={this.props.error}
                                loginWithMagicLink={
                                  this.props.loginWithMagicLink
                                }
                              />
                            )}
                          </Suspense>
                        )
                      }
                    />
                  </Switch>
                </React.Suspense>
              </Router>
            </ConfigProvider>
          </HelmetProvider>
        </I18nProvider>
      </DndProvider>
    );
  }
}

export default withAuthProvider(App);
